import type {
  DetectorCatalogResponse,
  DetectorRuleResponse,
  IncidentDomainResponse,
  Severity,
  SeverityRuleResponse,
  ThresholdRuleResponse,
} from '@prerender/diagnostics-api';

import { formatRequestCount } from './crawlerEvidenceFigures';
import { formatDetectedAt } from './formatDetectedAt';
import type { Loadable } from './loadable';

export const ORIGIN_BLOCKING_INCIDENT_TYPE = 're1';

const ORIGIN_BLOCKING_EVIDENCE_SCHEMA = 're1/1';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

export type BlockingFigures = {
  readonly totalRequests: number;
  readonly blockedRequests: number;
};

export type SeverityBand = {
  readonly severity: Severity;
  readonly fromShare: number;
  readonly toShare: number | null;
};

export type RateRules = {
  readonly windowDays: number;
  readonly bands: readonly SeverityBand[];
  readonly floorShare: number;
};

export type MeasurementPeriod = {
  readonly start: Date;
  readonly end: Date;
};

export function blockingFiguresOf(evidence: Record<string, unknown>): BlockingFigures | null {
  const { totalRequests, blockedRequests } = evidence;
  if (evidence.schema !== ORIGIN_BLOCKING_EVIDENCE_SCHEMA) return null;
  if (typeof totalRequests !== 'number' || typeof blockedRequests !== 'number') return null;

  return { totalRequests, blockedRequests };
}

export type IncidentBlockingFigures = BlockingFigures & {
  readonly affectedUrlCount: number;
  readonly lastMeasured: boolean;
  readonly unreadableDomainCount: number;
};

export const isRecovered = ({ state }: Pick<IncidentDomainResponse, 'state'>): boolean => state === 'no_longer_affected';

export function formatBlockedOfTotal({ blockedRequests, totalRequests }: BlockingFigures): string {
  return `${formatRequestCount(blockedRequests)} of ${formatRequestCount(totalRequests)}`;
}

export function incidentBlockingFiguresOf(domains: readonly IncidentDomainResponse[]): IncidentBlockingFigures | null {
  const stillAffected = domains.filter((row) => !isRecovered(row));
  const lastMeasured = stillAffected.length === 0;
  const counted = lastMeasured ? domains : stillAffected;
  const read = counted.flatMap((row) => {
    const figures = blockingFiguresOf(row.evidence);

    return figures === null ? [] : [{ ...figures, affectedUrlCount: row.affectedUrlCount }];
  });
  if (read.length === 0) return null;

  return {
    totalRequests: read.reduce((sum, figures) => sum + figures.totalRequests, 0),
    blockedRequests: read.reduce((sum, figures) => sum + figures.blockedRequests, 0),
    affectedUrlCount: read.reduce((sum, figures) => sum + figures.affectedUrlCount, 0),
    lastMeasured,
    unreadableDomainCount: counted.length - read.length,
  };
}

export function bandFor(rules: RateRules | null, severity: Severity): SeverityBand | undefined {
  return rules?.bands.find((band) => band.severity === severity);
}

const isSeverityRule = (rule: DetectorRuleResponse): rule is SeverityRuleResponse => rule.kind === 'severity';

const isWindowRule = (rule: DetectorRuleResponse): rule is ThresholdRuleResponse =>
  rule.kind === 'threshold' && rule.name === 'window';

function bandsOf(severityRules: readonly SeverityRuleResponse[]): SeverityBand[] {
  const lowestFirst = [...severityRules].sort((left, right) => left.share - right.share);

  return lowestFirst.map(({ severity, share }, position) => ({
    severity,
    fromShare: share,
    toShare: lowestFirst[position + 1]?.share ?? null,
  }));
}

export function rateRulesOf(catalog: Loadable<DetectorCatalogResponse>, incidentType: string): RateRules | null {
  if (catalog.status !== 'loaded') return null;
  const rules = catalog.data.detectors.find((candidate) => candidate.incidentType === incidentType)?.rules ?? [];
  const window = rules.find(isWindowRule);
  const severityRules = rules.filter(isSeverityRule);
  const floor = severityRules.find((rule) => rule.opensIncident);
  if (window === undefined || floor === undefined) return null;

  return { windowDays: window.value, bands: bandsOf(severityRules), floorShare: floor.share };
}

const asPercent = (share: number): number => Number((share * 100).toFixed(2));

export function bandRangeLabel({ fromShare, toShare }: SeverityBand): string {
  return toShare === null ? `${asPercent(fromShare)}% or more` : `${asPercent(fromShare)}–${asPercent(toShare)}%`;
}

export function floorLabel(floorShare: number): string {
  return `Under ${asPercent(floorShare)}%: no incident`;
}

export function formatMeasurementPeriod({ start, end }: MeasurementPeriod): string {
  return `${formatDetectedAt(start)} → ${formatDetectedAt(end)}`;
}

export function measurementPeriodOf(end: string, windowDays: number): MeasurementPeriod {
  const endInstant = new Date(end);

  return { start: new Date(endInstant.getTime() - windowDays * DAY_IN_MS), end: endInstant };
}
